import { twMerge } from "tailwind-merge";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  H3,
  H6,
} from "@sellar-io/components";
import {
  SupplierRelationshipDiscountListResponseType,
  SupplierRelationshipProductItemDiscountListResponseType,
} from "@sellar-io/api-v2/types";
import { useState } from "react";
import { Icon, SpinnerLegacy } from "../../../../../components";
import {
  TableLegacy,
  TableLegacyContainer,
  TableLegacyTHead,
  TableLegacyTR,
  TableLegacyTH,
  TableLegacyTBody,
  TableLegacyTD,
} from "../../../../../components/table/TableLegacy";
import { useSupplierRelationshipDiscountList } from "../../../../../hooks/api/v2/supplierRelationshipDiscount/useSupplierRelationshipDiscountList";
import { useSupplierRelationshipProductItemDiscountList } from "../../../../../hooks/api/v2/supplierRelationshipProductItemDiscount/useSupplierRelationshipProductItemDiscountList";
import { AddItemDiscountDialog } from "./dialogs/AddItemDiscountDialog";
import { EditItemDiscountDialog } from "./dialogs/EditItemDiscountDialog";
import {
  ItemDiscountCardContext,
  generateConnectionDiscountDescription,
} from "./BusinessConnectionItemDiscountsCard.shared";

export type Connection = {
  id: number;
  buyerName: string;
  retailerId: number;
};

export type ItemDiscountType =
  SupplierRelationshipProductItemDiscountListResponseType[number];

export type ContainerDiscountType =
  SupplierRelationshipDiscountListResponseType[number];

const DiscountRow = ({
  discount,
  onClickEditItemDiscount,
}: {
  discount: ItemDiscountType;
  onClickEditItemDiscount: (discount: ItemDiscountType) => void;
}) => {
  return (
    <TableLegacyTR key={discount.id}>
      <TableLegacyTD>
        {generateConnectionDiscountDescription(discount)}
      </TableLegacyTD>
      <TableLegacyTD className={"flex place-content-end"}>
        <Button
          color={"transparent"}
          onClick={() => onClickEditItemDiscount(discount)}
        >
          <Icon icon={"PENCIL"} />
        </Button>
      </TableLegacyTD>
    </TableLegacyTR>
  );
};

export const BusinessConnectionItemDiscountsCard = ({
  connection,
}: {
  connection: {
    id: number;
    buyerName: string;
    retailerId: number;
  };
}) => {
  const [editingItemDiscount, setEditingItemDiscount] =
    useState<ItemDiscountType>();

  const [isAddItemDiscountModalActive, setIsAddItemDiscountModalActive] =
    useState(false);

  const connectionItemDiscountListQuery =
    useSupplierRelationshipProductItemDiscountList({
      query: [
        {
          type: "SupplierRetailerConnectionId",
          modifier: "EQUALS",
          value: connection.id,
        },
      ],
    });

  const connectionDiscountListQuery = useSupplierRelationshipDiscountList({
    query: [
      {
        type: "SupplierRetailerConnectionId",
        modifier: "EQUALS",
        value: connection.id,
      },
    ],
  });

  return (
    <ItemDiscountCardContext.Provider
      value={{
        editingItemDiscount,
        setEditingItemDiscount,
        containerTypeDiscountList:
          connectionDiscountListQuery.data?.pages[0] ?? [],
        itemDiscountList: connectionItemDiscountListQuery.data?.pages[0] ?? [],
        connection,
      }}
    >
      <Card className="bg-white">
        <CardHeader
          className={twMerge("bg-white", "flex items-center justify-between")}
        >
          <div>
            <H3>Discounts by product item</H3>
            <H6>
              {connectionItemDiscountListQuery.data?.pages[0]?.length ?? "0"}{" "}
              discounts applied
            </H6>
          </div>

          <Button
            color={"secondary"}
            onClick={() => setIsAddItemDiscountModalActive(true)}
          >
            <Icon icon={"PLUS"} />
            Add discount
          </Button>
        </CardHeader>
        <CardContent>
          {connectionItemDiscountListQuery.isPending ? (
            <SpinnerLegacy />
          ) : connectionItemDiscountListQuery.isError ? (
            <div>Something went wrong</div>
          ) : (
            <>
              <TableLegacyContainer>
                <TableLegacy>
                  <TableLegacyTHead>
                    <TableLegacyTR>
                      <TableLegacyTH>Discount rule</TableLegacyTH>
                      <TableLegacyTH></TableLegacyTH>
                    </TableLegacyTR>
                  </TableLegacyTHead>
                  <TableLegacyTBody>
                    {connectionItemDiscountListQuery.data.pages.map((page) =>
                      page.map((discount) => (
                        <DiscountRow
                          key={discount.id}
                          discount={discount}
                          onClickEditItemDiscount={(discount) => {
                            setEditingItemDiscount(discount);
                          }}
                        />
                      )),
                    )}
                  </TableLegacyTBody>
                </TableLegacy>
              </TableLegacyContainer>
            </>
          )}
        </CardContent>

        <AddItemDiscountDialog
          active={isAddItemDiscountModalActive}
          onDismiss={() => setIsAddItemDiscountModalActive(false)}
        />
        <EditItemDiscountDialog
          active={!!editingItemDiscount}
          onDismiss={() => setEditingItemDiscount(undefined)}
          editingItemDiscount={editingItemDiscount}
        />
      </Card>
    </ItemDiscountCardContext.Provider>
  );
};
